import "./VideoMentionPill.css";
import { Button } from "@ui/Button.tsx";
import { usePendingImage } from "@ui/hooks/usePendingImage";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { Text } from "@ui/typography/Text.tsx";
import { onMount, Show } from "solid-js";
import {
	installMentionClipboard,
	videoMentionUrl,
} from "../utils/mentionClipboard";
import { isMentionClip } from "../utils/videoMentionPlayback.ts";
import type { MentionedVideoItem } from "./VideoMention.tsx";

type Props = {
	mention: MentionedVideoItem;
	/**
	 * `neutral` — grey chip with a border (clipboard).
	 * `brand` — borderless purple chip that sits inline in text (asked question).
	 * @default "neutral"
	 */
	tone?: "neutral" | "brand";
	/**
	 * When provided, a trailing remove control is shown. Omit for a read-only pill.
	 */
	onRemove?: () => void;
};

/**
 * A video mention chip: circular thumbnail + title, scissors between the two
 * when the mention is a clip rather than a whole video, and an optional remove
 * control. Shared by the clipboard and the read-only question view; the ask-box
 * pill is a separate imperative element because it lives inside a contenteditable.
 */
export const VideoMentionPill = (props: Props) => {
	// Copying a pill writes the link below in the QuestionBox's own form, so that
	// the clip survives being pasted into another app (see mentionClipboard).
	onMount(installMentionClipboard);

	const thumbnail = usePendingImage(() => props.mention.thumbnail_url);
	const label = () => props.mention.title ?? `Video ${props.mention.index}`;
	const tone = () => props.tone ?? "neutral";

	const PillFace = () => (
		<>
			<Show when={thumbnail.src()}>
				{(src) => (
					<img class="video-mention-pill__thumbnail" src={src()} alt="" />
				)}
			</Show>
			<Show when={isMentionClip(props.mention)}>
				<MaterialSymbol
					class="video-mention-pill__clip-symbol"
					symbol="content_cut"
					relativeSize={0.9}
				/>
			</Show>
			<Text
				class="video-mention-pill__label"
				size="small"
				color={tone() === "brand" ? "purple" : "fg1"}
				ellipsis
			>
				{label()}
			</Text>
		</>
	);

	return (
		<span
			class="video-mention-pill"
			classList={{ [`video-mention-pill--${tone()}`]: true }}
			title={label()}
		>
			<Show when={videoMentionUrl(props.mention)} fallback={<PillFace />}>
				{(href) => (
					// Not a tab stop of its own, and never followed: the link is here to
					// be copied. The remove control stays outside it, so the pill never
					// nests a button inside a link.
					<a
						class="video-mention-pill__link"
						href={href()}
						tabIndex={-1}
						draggable={false}
						onClick={(event) => event.preventDefault()}
					>
						<PillFace />
					</a>
				)}
			</Show>
			<Show when={props.onRemove}>
				<Button
					symbol="close"
					symbolColor="fg4"
					variant="ghost"
					theme="monochrome"
					size="small"
					aria-label={`Remove ${label()}`}
					onClick={() => props.onRemove?.()}
				/>
			</Show>
		</span>
	);
};
